import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useSlider } from '../../../hooks/useSlider';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import './combo-box-slider.scss';
import { ComboBox, IComboBox } from '../../molecules';
import { IComponent } from '../../../typings/generic-component';
import { IComboOption } from '../../molecules/combo-box/combo-box';
import { isValidUrl } from '../../../utils/urls-utils';

const DEFAULT_SCROLL_FADE_THRESHOLD = 25;

export interface IComboBoxSlider extends IComponent {
  comboBoxArray: IComboBox[];
}

const classNames = (cn: string): IClassNames =>
  makeClassNames({
    ComboBoxSlider: cn,
    ComboBoxSliderContainer: `${cn}__container`,
  });

const ComboBoxSlider: React.FC<IComboBoxSlider> = ({ comboBoxArray }) => {
  const cn = classNames('combo-box-slider');
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  useEffect(() => {
    const component = componentRef.current;
    const slider = sliderRef.current;
    if (component && slider) {
      component.style.setProperty('--scroll-opacity-start', isAtStart ? '0' : '1');
      component.style.setProperty('--scroll-opacity-end', isAtEnd ? '0' : '1');

      const handleScroll = () => {
        setIsAtStart(slider.scrollLeft < DEFAULT_SCROLL_FADE_THRESHOLD);
        setIsAtEnd(slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - DEFAULT_SCROLL_FADE_THRESHOLD);
      };

      slider.addEventListener('scroll', handleScroll);

      // Clean up the event listener when the component unmounts
      return () => {
        slider.removeEventListener('scroll', handleScroll);
      };
    }
    return undefined;
  }, [isAtStart, isAtEnd]);
  useSlider(sliderRef);

  const onClickComboBoxCallback = (e: IComboOption) => {
    const dataUrl = e?.url as string;
    if (isValidUrl(dataUrl)) {
      window.location.assign(dataUrl);
    }
  };
  const [openedComboBoxID, setOpenedComboBoxID] = useState<string | null>(null);
  return (
    <div className={cn.ComboBoxSlider} ref={componentRef}>
      <div className={cn.ComboBoxSliderContainer} ref={sliderRef}>
        {comboBoxArray &&
          comboBoxArray?.map((comboBox, index) => (
            <ComboBox
              setOpenedComboBoxID={setOpenedComboBoxID}
              openedComboBoxID={openedComboBoxID}
              comboBoxID={`combo-box-slider-item-${comboBox.placeholder || index}`}
              key={index}
              {...comboBox}
              onClickCallback={onClickComboBoxCallback}
            />
          ))}
      </div>
    </div>
  );
};

ComboBoxSlider.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
};

export default ComboBoxSlider;
